<script setup>
import { props as formGroupProps, useFormGroup } from '@phoenix/composables/components/form/formGroup';
import { computed, useSlots } from 'vue';

const slots = useSlots();
const hasIconSlot = computed(() => !!slots.icon);

const props = defineProps({
  ...formGroupProps,
  isFieldset: {
    type: Boolean,
    default: false,
  },
  fieldsetHorizontal: {
    type: Boolean,
    default: false,
  },
  fieldsetFieldsSpacing: {
    type: [String, Number],
    default: null,
  },
  hasFieldsetFieldsWrapper: {
    type: Boolean,
    default: true,
  },
  customFieldsetClasses: {
    type: [String, Array, Object],
    default: null,
  },
});

const {
  _shouldDisplayCharLeft,
  charactersLeft,
  displayedErrors,
  errorId,
  field,
  fieldUniqueId,
  hasHelpSlot,
  hasLegendSlot,
  helpId,
  isHidden,
  isInvalid,
  labelId,
} = useFormGroup(props);

const labelClasses = computed(() => [
  'input-label form-field__label cluster cluster--spacing-2',
  {
    'input-label--error': isInvalid,
    'input-label--disabled': field.disabled,
  },
]);

const fieldsetFieldsClasses = computed(() => [
  'fieldset__fields',
  props.customFieldsetClasses
    ? props.customFieldsetClasses
    : {
        [`stack stack--spacing-${props.fieldsetFieldsSpacing ?? 2}`]:
          !props.fieldsetHorizontal && props.hasFieldsetFieldsWrapper,
        [`cluster cluster--spacing-${props.fieldsetFieldsSpacing ?? 4}`]:
          props.fieldsetHorizontal && props.hasFieldsetFieldsWrapper,
      },
]);
</script>

<template>
  <div v-if="!isFieldset" :key="`form-group-${fieldUniqueId}`" class="form-control stack stack--spacing-3" data-cs-mask>
    <div class="form-control__wrapper stack stack--spacing-3">
      <label v-if="field.label && !isHidden && showLabel" :id="labelId" :for="fieldUniqueId" :class="labelClasses">
        <span class="input-label__text" v-html="field.label" />
        <span
          v-if="field.required"
          class="input-label__required-indicator"
          aria-hidden="true"
          data-cy="form-input-required"
        >
          *
        </span>
      </label>

      <template v-if="hasIconSlot">
        <div class="input-wrapper input-wrapper--offset-end">
          <slot />

          <slot name="icon" />
        </div>
      </template>
      <template v-else>
        <slot />
      </template>
    </div>

    <div v-if="field.help || hasHelpSlot || isInvalid || _shouldDisplayCharLeft" class="stack stack--spacing-1">
      <span v-if="field.help" :id="helpId" class="form-helper-text form-field__helper-text" v-html="field.help" />
      <slot v-else name="help" />

      <span v-if="_shouldDisplayCharLeft" class="form-helper-text form-field__helper-text">
        {{ $t('Form.charactersLeft', { charactersLeft }) }}
      </span>

      <span v-if="isInvalid" :id="errorId" class="form-helper-text form-field__helper-text">
        {{ displayedErrors }}
      </span>
    </div>

    <slot name="after" />
  </div>

  <fieldset v-else class="form-control fieldset stack stack--spacing-4">
    <legend
      v-if="hasLegendSlot || (field.label && !isHidden && showLabel)"
      class="fieldset__legend input-label stack stack--spacing-1"
    >
      <slot name="legend">
        <span>
          <span v-html="field.label" />

          <span
            v-if="field.required"
            class="input-label__required-indicator"
            aria-hidden="true"
            data-cy="form-input-required"
          >
            *
          </span>
        </span>
      </slot>

      <span v-if="isInvalid" :id="errorId" class="form-helper-text fieldset__helper-text">
        {{ displayedErrors }}
      </span>
    </legend>

    <div :class="fieldsetFieldsClasses">
      <slot />
    </div>
  </fieldset>
</template>
